import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiClientDrawerV2 as apiClient } from '@lift-admin/utils';
import { Info } from 'lucide-react';
import { Toastify } from 'lift-ui';
import { actionLog } from '../../../App';
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from '@lift-admin/utils/logChangeFormatter';
import {
  Button,
  DataTable,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Input,
  Label,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const customComponentFieldSpecs: FieldSpec[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
];

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const CustomComponents = ({
  toggle,
  searchText,
  openDialog,
  setOpenDialog,
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemBackUp] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const [currentId, setCurrentId] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // Snapshot of the row being edited, used to log before -> after changes.
  const editOriginalRef = useRef<{ name?: string; description?: string }>({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const getCustomComponents = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClient.get('/admin/custom-components', {
        params: {
          user_id: user.id,
          is_admin: true,
          is_team: true,
        }
      });

      // New API returns array directly, no nested response
      const customComponents = res?.data?.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );

      setDrawerItems(customComponents);
      setDrawerItemBackUp(customComponents);
    } catch {
      Toastify('error', 'Custom Components not fetched');
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    getCustomComponents();
  }, []);

  const rowData = useMemo(() => drawerItem, [drawerItem]);

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return rowData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [rowData, page, rowsPerPage]);

  const totalPages = Math.ceil(rowData.length / rowsPerPage);

  const searchResults = (query: string) => {
    const filteredUsers = [...drawerItemBackUp];
    const results = filteredUsers.filter((user) =>
      user.name?.toLowerCase().includes(query.toLowerCase())
    );
    setDrawerItems(results);
    setPage(0); // Reset to first page on search
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText, drawerItemBackUp]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setName('');
    setDescription('');
  };

  const handleAddChannel = async () => {
    try {
      if (!name || name.length === 0) {
        Toastify('error', 'Please enter correct fields');
        return;
      }
      const payload = {
        name,
        description,
      };
      const res = await apiClient.post(
        '/drawer/v1/admin/drawer/structure/custom-component',
        payload
      );
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const componentId = res?.data?.id;
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: buildSummary('Custom component', name, 'created'),
          status: res.status,
          method: res.config?.method,
          url: res.config?.url,
          category: 'drawer',
          action: 'create_custom_component',
          custom_component_id: componentId ?? null,
          custom_component_name: name,
          user_id: user?.id,
          user_name: user?.name,
          details: buildDetails(payload, customComponentFieldSpecs, {
            skipEmpty: true,
          }),
        }),
        false
      );
      getCustomComponents();
      setName('');
      setDescription('');
      setOpenDialog(false);
      Toastify('success', 'Custom Component added successfully');
    } catch (err: any) {
      setOpenDialog(false);
      Toastify('error', 'Custom Component not added');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to create custom component "${name}"`,
          status: err?.response?.status || 500,
          method: 'POST',
          url: '/drawer/v1/admin/drawer/structure/custom-component',
          category: 'drawer',
          action: 'create_custom_component',
          custom_component_name: name,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleDelete = async (id: any) => {
    const existing =
      drawerItemBackUp.find((item: any) => String(item?.id) === String(id)) ||
      drawerItem.find((item: any) => String(item?.id) === String(id));
    const componentName = existing?.name;
    try {
      if (!id || isNaN(id)) {
        Toastify(
          'error',
          'Invalid Custom Component ID. Please select correct component'
        );
        return;
      }
      const res = await apiClient.delete(
        `/drawer/v1/admin/drawer/structure/custom-component/${id}`
      );

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: buildSummary('Custom component', componentName, 'deleted'),
          status: res.status,
          method: res.config?.method,
          url: res.config?.url,
          category: 'drawer',
          action: 'delete_custom_component',
          custom_component_id: id,
          custom_component_name: componentName,
          user_id: user?.id,
          user_name: user?.name,
          details: [
            { label: 'Component ID', value: String(id ?? 'Not Set') },
            ...buildDetails(
              {
                name: componentName,
                description: existing?.description,
              },
              customComponentFieldSpecs,
              { skipEmpty: true }
            ),
          ],
        }),
        false
      );
      getCustomComponents();
      Toastify('success', 'Item Deleted successfully');
    } catch (err: any) {
      Toastify('error', 'Custom Component not deleted');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to delete custom component "${componentName || id}"`,
          status: err?.response?.status || 500,
          method: 'DELETE',
          url: `/drawer/v1/admin/drawer/structure/custom-component/${id}`,
          category: 'drawer',
          action: 'delete_custom_component',
          custom_component_id: id,
          custom_component_name: componentName,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleEdit = async () => {
    if (!currentId || isNaN(Number(currentId))) {
      Toastify('error', 'Invalid component ID. Please select correct component');
      return;
    }

    if (!name || name.length === 0) {
      Toastify('error', 'Please enter correct fields');
      return;
    }

    try {
      const res = await apiClient.put(
        `/drawer/v1/admin/drawer/structure/custom-component/${currentId}`,
        {
          name: name,
          description: description,
        }
      );
      const original = editOriginalRef.current || {};
      const previousComponent = {
        name: original.name ?? '',
        description: original.description ?? '',
      };
      const nextComponent = {
        name: name ?? '',
        description: description ?? '',
      };
      const changes = diffFields(
        previousComponent,
        nextComponent,
        customComponentFieldSpecs
      );
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: buildSummary(
            'Custom component',
            name || original.name,
            'updated',
            changes.length
          ),
          status: res.status,
          method: res.config?.method,
          url: res.config?.url,
          category: 'drawer',
          action: 'update_custom_component',
          custom_component_id: currentId,
          custom_component_name: name || original.name,
          user_id: user?.id,
          user_name: user?.name,
          changes,
        }),
        false
      );
      Toastify('success', 'Item updated successfully');
      getCustomComponents();
      setName('');
      setDescription('');
      handleEditDialogClose();
    } catch (err: any) {
      Toastify('error', 'Item not updated');
      console.error(err);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const original = editOriginalRef.current || {};
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to update custom component "${name || original.name || currentId}"`,
          status: err?.response?.status || 500,
          method: 'PUT',
          url: `/drawer/v1/admin/drawer/structure/custom-component/${currentId}`,
          category: 'drawer',
          action: 'update_custom_component',
          custom_component_id: currentId,
          custom_component_name: name || original.name,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditOpen = (data: any) => {
    setCurrentId(data?.id);
    setName(data?.name || '');
    setDescription(data?.description || '');
    editOriginalRef.current = { name: data?.name || '', description: data?.description || '' };
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setName('');
    setDescription('');
    setCurrentId(null);
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-end mb-2 text-[#000]">
          <Info className="h-4 w-4" />
          <p className="ml-2 text-sm">
            Please create a{" "}
            <a href="https://feedback.lift.newengen.com/" target="_blank" rel="noopener noreferrer" className="underline text-blue-500 hover:no-underline">ticket</a>
            {" "}for any additions or modifications
          </p>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          These are the components that enable the custom component functionality in Looker Studio report builder.
        </p>
        <div className="flex-1 min-h-0 flex flex-col">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="100%"
            columns={[
              { key: 'name', header: 'Name', accessor: 'name', width: '200px', render: (_v: any, row: any) => <span className="font-medium">{row.name}</span> },
              { key: 'description', header: 'Description', accessor: (row: any) => row.description || '--', width: '300px', render: (_v: any, row: any) => <span className="text-gray-600">{row.description || '--'}</span> },
              { key: 'created_at', header: 'Created At', accessor: (row: any) => formatDate(row.created_at), width: '140px', render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.created_at)}</span> },
              { key: 'updated_at', header: 'Updated At', accessor: (row: any) => formatDate(row.updated_at), width: '140px', render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.updated_at)}</span> },
            ]}
            data={paginatedData}
            loading={loadData}
            emptyMessage="No custom components found."
            stickyHeader
            showActions={false}
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, totalPages),
              pageSize: rowsPerPage,
              totalItems: rowData.length,
              onPageChange: (p) => setPage(p - 1),
              onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        </div>
      </div>

      {/* Create Sheet */}
      <Sheet open={openDialog} onOpenChange={(open) => !open && handleDialogClose()}>
        <SheetContent className="flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Add New Custom Component</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button onClick={handleDialogClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Sheet */}
      <Sheet open={openEditDialog} onOpenChange={(open) => !open && handleEditDialogClose()}>
        <SheetContent className="flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Edit Custom Component</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button onClick={handleEditDialogClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CustomComponents;
